import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useLazyUser } from "$global-providers/user-provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import type { QueryPullImagingInstanceArgs } from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { ImagingInstance as RxImagingInstance } from "../collections/imaging-instance";
import type { RxDBCollections } from "../provider";
import {
  ImagingInstancePullDocument,
  type WholeSyncableImagingInstanceFragment,
} from "./__generated__/imaging-instance.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  IMAGING_REPLICATION_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateImagingInstance = ({
  db,
  collection,
  dicomStudyId: dicomStudyIdProp,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxImagingInstance>;
  /** When provided and no URL-derived elephantPatientId is present, pull by dicomStudyId instead. */
  dicomStudyId?: string | null;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const elephantPatientId =
    matchPat?.params.patientId ?? query.patientUuid ?? null;
  const [featureFlagEnabled] = useFeatureFlag(
    featureFlags.pocus.imagingStudySync,
  );
  const role = useLazyUser()?.role;
  const active =
    featureFlagEnabled &&
    isReplicationRoleAllowed(IMAGING_REPLICATION_ROLES, role);

  // Use dicomStudyId only when there is no URL-derived elephantPatientId.
  const dicomStudyId = !elephantPatientId ? (dicomStudyIdProp ?? null) : null;
  // The active filter key determines checkpoint storage and replication identity.
  const activeKey = elephantPatientId ?? dicomStudyId;

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableImagingInstanceFragment> | undefined
  >(() => {
    if (!activeKey) return undefined;
    return {
      query: ImagingInstancePullDocument,
      reSyncInterval: { minutes: 30 },
      ...createPersistedCheckpointPullBuilders<
        QueryPullImagingInstanceArgs,
        WholeSyncableImagingInstanceFragment
      >({
        buildInputVariables: (checkpoint, batchSize) => ({
          input: {
            limit: batchSize,
            filters: dicomStudyId
              ? { dicomStudyId }
              : { elephantPatientId: elephantPatientId! },
            checkpoint,
          },
        }),
        buildPullCheckpoint: (doc) => ({
          id: doc.id,
          updatedAt: doc.meta.updatedAt,
        }),
        entityId: activeKey,
        db,
        collectionName: collection?.name,
      }),
    };
  }, [elephantPatientId, dicomStudyId, activeKey, db, collection]);

  const replicationResult = useGqlReplication<
    RxImagingInstance,
    WholeSyncableImagingInstanceFragment,
    null
  >({
    db,
    collection,
    pull: active && activeKey ? pull : undefined,
    mapGqlToRx: mapGqlImagingInstanceToRxImagingInstance,
    batchSize: 50,
    expireData: true,
    metadataLocalStorageKeySuffix: `imaging-instance-${activeKey}`,
    // Give each caller (global vs per-encounter) a unique replicationIdentifier so
    // they don't share RxDB-internal state when mounted simultaneously.
    replicationIdentifierSuffix: activeKey ?? "no-key",
    enabled: active && !!activeKey,
  });

  return replicationResult;
};

export const mapGqlImagingInstanceToRxImagingInstance = (
  imagingInstance: WholeSyncableImagingInstanceFragment,
): WithDeleted<RxImagingInstance> => ({
  id: imagingInstance.id,
  imagingStudyAccessionNumber: imagingInstance.imagingStudyAccessionNumber,
  // PNE-221: study scope for instances without an accession number (GE VScan Air)
  dicomStudyId: imagingInstance.dicomStudyId ?? null,
  elephantPatientId: imagingInstance.elephantPatientId,
  externalId: imagingInstance.externalId,
  previewS3Key: imagingInstance.previewS3Key,
  thumbnailS3Key: imagingInstance.thumbnailS3Key,
  // PNE-212: DICOM preset metadata — null if absent or non-GE device
  dicomPreset: imagingInstance.dicomPreset ?? null,
  physicalDeltaMm: imagingInstance.physicalDeltaMm ?? null,
  numberOfFrames: imagingInstance.numberOfFrames ?? null,
  frameRate: imagingInstance.frameRate ?? null,
  cineStatus: imagingInstance.cineStatus ?? null,
  cineS3Key: imagingInstance.cineS3Key ?? null,
  cineDurationMs: imagingInstance.cineDurationMs ?? null,
  cineSizeBytes: imagingInstance.cineSizeBytes ?? null,
  // PNE-427: DICOM device identity — null when the PACS reports unknown/empty
  deviceManufacturer: imagingInstance.deviceManufacturer ?? null,
  deviceModelName: imagingInstance.deviceModelName ?? null,
  meta: {
    createdAt: imagingInstance.meta.createdAt,
    createdByUserId: imagingInstance.meta.createdByUserId,
    updatedAt: imagingInstance.meta.updatedAt,
    updatedByUserId: imagingInstance.meta.updatedByUserId,
  },
  _deleted: imagingInstance.meta.deleted,
});

gql`
  fragment WholeSyncableImagingInstance on SyncableImagingInstance {
    id
    imagingStudyAccessionNumber
    # PNE-221: study scope for instances without an accession number
    dicomStudyId
    externalId
    previewS3Key
    thumbnailS3Key
    elephantPatientId
    # PNE-212: DICOM preset metadata
    dicomPreset
    physicalDeltaMm
    numberOfFrames
    frameRate
    cineStatus
    cineS3Key
    cineDurationMs
    cineSizeBytes
    # PNE-427: DICOM device identity
    deviceManufacturer
    deviceModelName
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query ImagingInstancePull($input: PullImagingInstanceInput!) {
    pullImagingInstance(input: $input) {
      documents {
        ...WholeSyncableImagingInstance
      }
    }
  }
`;
